/**
 * API Luyện giao tiếp với AI — Gemini Live (plan 2026-10-03).
 * Mirror BE `apps.learning.conversation_practice_views`.
 */
import { apiClient } from "@/api/client";

export type ConversationStatus = "active" | "evaluating" | "evaluated" | "failed";

export interface TranscriptItem {
  role: "user" | "ai";
  text: string;
  /** Giây tính từ đầu phiên. */
  t: number;
}

export interface ConversationCriteria {
  pronunciation?: number;
  fluency?: number;
  grammar?: number;
  vocabulary?: number;
  interaction?: number;
}

export interface ConversationSession {
  id: number;
  course_slug: string;
  course_name: string;
  language: string;
  status: ConversationStatus;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number;
  transcript: TranscriptItem[];
  has_audio: boolean;
  /** 0–100 (FE quy đổi thang 10 qua ScoreTag). */
  score: number | null;
  criteria: ConversationCriteria;
  feedback: string;
  error_message: string;
  evaluated_at: string | null;
}

export interface CreateSessionResponse {
  session: ConversationSession;
  live: { token: string; model: string; expires_at: string };
  max_seconds: number;
}

const BASE = "/learning/conversation-practice/sessions/";

export async function createConversationSession(courseSlug: string) {
  const res = await apiClient.post<CreateSessionResponse>(BASE, { course_slug: courseSlug });
  return res.data;
}

export async function submitConversationEvaluation(
  id: number,
  wav: Blob,
  transcript: TranscriptItem[],
  durationSeconds: number,
) {
  const form = new FormData();
  form.append("audio", wav, "conversation.wav");
  form.append("transcript", JSON.stringify(transcript));
  form.append("duration_seconds", String(Math.round(durationSeconds)));
  const res = await apiClient.post<ConversationSession>(`${BASE}${id}/evaluate/`, form);
  return res.data;
}

/** Phiên `failed` → chấm lại bằng audio đã lưu. */
export async function retryConversationEvaluation(id: number) {
  const res = await apiClient.post<ConversationSession>(`${BASE}${id}/evaluate/`, {});
  return res.data;
}

export async function getConversationSession(id: number) {
  const res = await apiClient.get<ConversationSession>(`${BASE}${id}/`);
  return res.data;
}

export async function listConversationSessions(courseSlug: string) {
  const res = await apiClient.get<ConversationSession[]>(BASE, { params: { course: courseSlug } });
  return res.data;
}

/** Object URL bản ghi (cần JWT) — caller revoke khi xong. */
export async function fetchConversationAudioUrl(id: number) {
  const res = await apiClient.get<Blob>(`${BASE}${id}/audio/`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}
